/**
 * The Account Executive records a client's transfer notice (owner, 2026-10-31): only for a
 * client on their own roster (the server refuses any other), for the series the client can
 * contribute to. It moves no money. Agents no longer attribute transfers: operations do.
 */
import { useEffect, useState } from 'react';
import DepositNoticeCard from '../client/DepositNoticeCard';
import fundService from '../../services/fund.service';
import type { NoticeSeries } from '../client/depositNotice.helpers';
import { colors } from '../../styles/theme';

export default function AgentDepositNotice({ clientId, onRoster }: Readonly<{
  clientId: string;
  onRoster: boolean;
}>): JSX.Element | null {
  const [series, setSeries] = useState<NoticeSeries[] | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!onRoster) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fundService.getFundInstructions(clientId);
        if (!cancelled) setSeries(data.fundable_products ?? []);
      } catch {
        // A client who cannot contribute yet (identity not approved, no market) has no series.
        if (!cancelled) setUnavailable(true);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, onRoster]);

  if (!onRoster) return null;
  if (unavailable) {
    return (
      <p className="text-xs mt-6" style={{ color: colors.mutedSoft }}>
        This client cannot contribute yet, so no transfer notice can be recorded.
      </p>
    );
  }
  if (series === null) return null;
  return <DepositNoticeCard clientId={clientId} series={series} audience="agent" />;
}
